import { HttpClient } from '@angular/common/http';
import { DOCUMENT, EnvironmentInjector, Injectable, inject, runInInjectionContext } from '@angular/core';
import { initializeApp } from '@angular/fire/app';
import { getMessaging, getToken, isSupported, onMessage } from '@angular/fire/messaging';
import { Subject, firstValueFrom, map, switchMap } from 'rxjs';

import { skipHttpErrorPage } from '@dmb/dmb-ui';

import { DmbEnvironment } from '../dmb-environment';
import {
  NotificationEvent,
  NotificationSettings,
  PushNotification,
  isNotificationEvent,
  isNotificationPreferencesResponse,
  isPushNotification,
} from '../model/notification-settings';
import { PopupCenterService } from '../popup-center/popup-center.service';
import { UserService } from '../user';

/**
 * Manages notification service worker and settings.
 *
 * Checks user settings on initialization and registers or unregisters the service worker accordingly.
 * The service worker requires a secure context. The app has to run on localhost or via SSL for that.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private env = inject(DmbEnvironment);
  private http = inject(HttpClient);
  private userService = inject(UserService);
  private notification = inject(DOCUMENT).defaultView?.Notification;
  private navigator = inject(DOCUMENT).defaultView?.navigator;
  private popupCenter = inject(PopupCenterService);
  private envInjector = inject(EnvironmentInjector);

  private knownMessages = new Set<string>();

  private swFile = this.env.production ? '/firebase-messaging-sw-prod.js' : '/firebase-messaging-sw.js';

  private firebaseSetup = isSupported().then((sup) => {
    if (sup) {
      runInInjectionContext(this.envInjector, () => {
        initializeApp(this.env.firebaseConfig);

        const messaging = getMessaging();
        onMessage(messaging, (mes) => {
          if (isPushNotification(mes) && !this.knownMessages.has(mes.data.eventId)) {
            this.knownMessages.add(mes.data.eventId);

            this.messages.next(mes);
            this.popupCenter.addPopup({ title: mes.notification.body, timeout: 5000, type: 'info' });

            this.navigator?.serviceWorker.controller?.postMessage({
              type: 'foreground_notification',
              id: mes.data.eventId,
              notification: mes.notification,
            });
          }
        });
      });
    }

    return sup;
  });

  // Messages received from firebase since initializing the service
  messages = new Subject<PushNotification>();

  constructor() {
    this.getSettings().then((s) => this.updateRegistration(s));

    if (BroadcastChannel) {
      const channel = new BroadcastChannel('background_notifications');
      channel.addEventListener('message', (event) => {
        if (isPushNotification(event.data)) {
          this.messages.next(event.data);
        } else {
          console.error('Received malformed notification', event);
        }
      });
    } else {
      console.warn('BroadcastChannel not supported. Notification list may not update correctly');
    }
  }

  // Load current settings from the server
  getSettings(): Promise<NotificationSettings> {
    return firstValueFrom(
      this.userService.v2AccessToken.pipe(
        switchMap((token) =>
          this.http.get(this.env.v2Target + '/api/v2/users/notification-preferences', {
            context: skipHttpErrorPage(),
            headers: { Authorization: token },
          }),
        ),
        map((result) => {
          if (!isNotificationPreferencesResponse(result)) {
            throw new Error('Unknown response shape');
          }

          return result.topics;
        }),
      ),
    );
  }

  // Send given settings to the server. May update service worker registration.
  async updateSetting(newSettings: { code: string; mobile: boolean; web: boolean; email: boolean | undefined }[]) {
    const preferences: Record<string, string> = {};

    newSettings.forEach(({ code, mobile, web, email }) => {
      preferences[`${code}.mobile`] = String(mobile);
      preferences[`${code}.web`] = String(web);
      preferences[`${code}.email`] = String(email ?? false);
    });

    await firstValueFrom(
      this.userService.v2AccessToken.pipe(
        switchMap((token) =>
          this.http.post(
            this.env.v2Target + '/api/v2/users/notification-preferences',
            { preferences },
            { headers: { Authorization: token }, withCredentials: true },
          ),
        ),
      ),
    );

    await this.updateRegistration(newSettings);
  }

  private updateRegistration(settings: NotificationSettings | { web: boolean }[]) {
    const anyWebNotificationsEnabled = settings.some((topic) =>
      'channelSettings' in topic ? topic.channelSettings.web : topic.web,
    );

    if (anyWebNotificationsEnabled) {
      return this.register();
    } else {
      return this.unregister();
    }
  }

  private async register() {
    try {
      await firstValueFrom(
        this.userService.v2AccessToken.pipe(
          switchMap(async (token) => {
            if (!(await this.firebaseSetup)) {
              throw new Error('Notifications not supported');
            }

            if ((await this.notification?.requestPermission()) !== 'granted') {
              throw new Error('No notification permission');
            }

            if (this.navigator === undefined) {
              throw new Error('Not in a window context');
            }

            const serviceWorkerRegistration = await this.navigator.serviceWorker.register(this.swFile, {
              type: 'classic',
            });
            const firebaseToken = await getToken(getMessaging(), {
              serviceWorkerRegistration,
            });

            this.navigator.serviceWorker.ready.then(() => {
              if (!this.navigator?.serviceWorker.controller) {
                const sw = serviceWorkerRegistration.active;
                if (sw) {
                  sw.postMessage('init_notifications');
                } else {
                  console.error('No active Service Worker', serviceWorkerRegistration);
                }
              }
            });

            return { token, firebaseToken };
          }),
          switchMap(({ token, firebaseToken }) =>
            this.http.post(
              this.env.v2Target + '/api/v2/notifications/register-device',
              { firebaseToken, deviceType: 'web' },
              { headers: { Authorization: token } },
            ),
          ),
        ),
      );
    } catch (err) {
      console.error('Unable to register notification service worker', err);
    }
  }

  private async unregister() {
    const registration = await this.navigator?.serviceWorker.getRegistration();
    registration?.unregister();
  }

  // Load past notification events from api/v2
  loadNotifications() {
    return firstValueFrom(
      this.userService.v2AccessToken.pipe(
        switchMap((token) =>
          this.http.get(this.env.v2Target + '/api/v2/notifications/events', {
            context: skipHttpErrorPage(),
            headers: { Authorization: token },
          }),
        ),
        map((response) => {
          if (!Array.isArray(response)) {
            throw new Error('Can not decode NotificationEvents');
          }

          const validEvents: NotificationEvent[] = [];

          // Going back to front, because we want the newest first
          for (let i = response.length - 1; i >= 0; i--) {
            const entry = response[i];

            if (isNotificationEvent(entry)) {
              validEvents.push(entry);
              this.knownMessages.add(entry.eventId);
            } else {
              console.warn('Unknown NotificationEvent type', entry);
            }
          }

          return validEvents;
        }),
      ),
    );
  }

  /**
   * @returns ids of updated notifications
   */
  markAsRead(ids: string[]): Promise<string[]> {
    const promises = ids.map((id) =>
      firstValueFrom(
        this.userService.v2AccessToken.pipe(
          switchMap((token) =>
            this.http.post(`${this.env.v2Target}/api/v2/notifications/events/${id}/status/read`, null, {
              headers: { Authorization: token },
            }),
          ),
        ),
      ).then(() => id),
    );

    return Promise.all(promises);
  }
}
